import * as THREE from 'three';

// The glow around the sun: a camera-facing plane with a smooth radial falloff, added on top of the sky.
//
// It replaces a flat translucent sphere, which drew a hard-edged pale disc around the sun's disc (a
// fried egg). Real glare has no edge: a bright core that falls off roughly as an inverse square of the
// distance from the sun, plus a faint wide veil. The plane reaches zero at its rim, so nothing is cut.

/** Glow radius in sun-disc radii (the plane's half-width). */
export const SUN_GLARE_RADIUS = 12;
const SUN_GLARE_NEAR = 0.96;

const VERT = /* glsl */ `
varying vec2 vUv;
varying vec3 vWorld;
void main() {
  vUv = uv;
  vWorld = (modelMatrix * vec4(position, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// How much of the sun the cloud in front of it lets through, in the fragment's own direction: the sky probe's alpha (how much light the cloud there lets through)
// through a stricter curve than the stars get (a disc is visible through far less cloud than a star). Read in the shader, per pixel, so the disc and glare are hidden
// by the same cloud that is drawn over them, with no read-back from the GPU and no lag. Without the probe, uSeen (from the cloud cells or the dome's estimate) stands in.
const SEEN = /* glsl */ `
uniform samplerCube uCloudT;
uniform float uCloudOn;
uniform float uSeen;
varying vec3 vWorld;
float sunSeenHere() {
  return uCloudOn > 0.5 ? min(uSeen, smoothstep(0.3, 0.9, textureCube(uCloudT, normalize(vWorld - cameraPosition)).a)) : uSeen;
}
`;

/** The sun's disc: additive, unlit; its colour is the beam's hue; hidden by cloud in its own direction (see SEEN). */
export function makeSunDiscMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: /* glsl */ `
varying vec3 vWorld;
void main() {
  vWorld = (modelMatrix * vec4(position, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`,
    fragmentShader: /* glsl */ `
uniform vec3 uColor;
uniform float uSrgbOut;
${SEEN}
vec3 encodeSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}
void main() {
  float ct = sunSeenHere();
  vec3 c = uColor * ct * ct;                               // squared: the output curve lifts small values, and a disc behind thick cloud should vanish
  if (uSrgbOut > 0.5) c = encodeSrgb(c);
  gl_FragColor = vec4(c, 1.0);
}`,
    uniforms: { uColor: { value: new THREE.Color(1, 1, 1) }, uSrgbOut: { value: 1 }, uCloudT: { value: null as THREE.Texture | null }, uCloudOn: { value: 0 }, uSeen: { value: 1 } },
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  });
}

const FRAG = /* glsl */ `
uniform vec3  uColor;
uniform float uOpacity;
uniform float uSrgbOut;
varying vec2  vUv;
${SEEN}

vec3 encodeSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

void main() {
  // r = 1 / ${SUN_GLARE_RADIUS.toFixed(1)} is the edge of the sun's disc.
  float r = length(vUv * 2.0 - 1.0);
  float core = 1.0 / (1.0 + pow(r / 0.11, 2.0));          // tight bright glare, ~inverse square
  float veil = exp(-pow(r / 0.42, 2.0));                  // faint wide haze
  float a = uOpacity * (0.55 * core + 0.30 * veil) * (0.08 + 0.92 * sunSeenHere());   // behind thick cloud only a faint soft patch is left; the cloud's own lighting carries the rest
  a *= 1.0 - smoothstep(0.6, 1.0, r);                     // reaches zero before the rim
  vec3 c = uSrgbOut > 0.5 ? encodeSrgb(uColor) : uColor;
  gl_FragColor = vec4(c, a);
}
`;

export class SunGlare {
  readonly mesh: THREE.Mesh;
  private geo = new THREE.PlaneGeometry(2, 2);
  private mat: THREE.ShaderMaterial;

  constructor() {
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: { uColor: { value: new THREE.Color(1, 1, 1) }, uOpacity: { value: 0 }, uSrgbOut: { value: 1 }, uCloudT: { value: null as THREE.Texture | null }, uCloudOn: { value: 0 }, uSeen: { value: 1 } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  /** Per frame. `x, y, z` is the sun's position, `discRadius` its drawn radius in world units. */
  update(camera: THREE.Vector3, x: number, y: number, z: number, discRadius: number,
         color: THREE.Color, opacity: number, visible: boolean, srgbOut: boolean) {
    // Drawn a little nearer than the sky radius (same direction, same angular size) so the moon's
    // disc, which sits at the sky radius and writes depth, cannot cut a hole in the glow.
    const k = SUN_GLARE_NEAR;
    this.mesh.position.set(camera.x + (x - camera.x) * k, camera.y + (y - camera.y) * k, camera.z + (z - camera.z) * k);
    this.mesh.scale.setScalar(discRadius * SUN_GLARE_RADIUS * k);
    this.mesh.lookAt(camera);
    this.mat.uniforms.uColor.value.copy(color);
    this.mat.uniforms.uOpacity.value = opacity;
    this.mat.uniforms.uSrgbOut.value = srgbOut ? 1 : 0;
    this.mesh.visible = visible && opacity > 0.003;
  }

  /** The sky probe (Sky.probe.texture) and, for when there is none, the share of the sun that shows (0..1). */
  setCloud(tex: THREE.Texture | null, seen: number) {
    this.mat.uniforms.uCloudT.value = tex; this.mat.uniforms.uCloudOn.value = tex ? 1 : 0; this.mat.uniforms.uSeen.value = seen;
  }

  dispose() { this.geo.dispose(); this.mat.dispose(); }
}
